import Link from "next/link";
import {
  FaStar,
  FaSearch,
  FaRegStar,
  FaFacebookF,
  FaCalendarAlt,
  FaRegUser,
} from "react-icons/fa";
import { productSlug, getDiscountPrice } from "@/lib/product";
import FollowUs from "@/components/followUs";
import SafeImage from "@/components/SafeImage";

// The admin stores uploads under <CMS_API_BASE>/uploads/<path>. The CMS
// normalizer returns the relative path as-is, so when rendering we need
// to rewrite it into an absolute URL the Next.js frontend can fetch.
const CMS_API_BASE =
  process.env.CMS_API_BASE ||
  process.env.NEXT_PUBLIC_CMS_API_BASE ||
  "http://localhost:8000";

const resolveBlogImage = (blog) => {
  const raw =
    blog?.featured_image ||
    (blog?.thumbImg ? `/img/blog/${blog.thumbImg}` : "");
  if (!raw) return "";
  // Already absolute (http/https) — use as-is.
  if (/^https?:\/\//i.test(raw)) return raw;
  // Server-relative on the Next.js site — use as-is.
  if (raw.startsWith("/")) return raw;
  // Otherwise it's a CMS-side relative path (e.g. "uploads/foo.jpg") —
  // prefix with the admin base so the image loads from there.
  return `${CMS_API_BASE.replace(/\/$/, "")}/${raw.replace(/^\/+/, "")}`;
};

// Resolve an author avatar URL. Mirrors resolveBlogImage(): absolute URLs
// pass through, server-relative paths pass through, CMS-side relative paths
// get prefixed with the admin base. Returns "" when no avatar is set so the
// <img> simply renders its alt text instead of a broken-image icon.
const resolveAuthorImage = (author) => {
  const raw = author?.avatar || author?.avatar_url || "";
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/")) return raw;
  return `${CMS_API_BASE.replace(/\/$/, "")}/${raw.replace(/^\/+/, "")}`;
};

const BlogSideBar = ({
  popularProducts,
  topRatedProducts,
  latestdBlogs = [],
  showAgent = true,
  showBookForm = true,
  showTopRated = true,
  showAuthors = true,
  showTwitter = true,
  showFollowUs = true,
  activeCategory = null,
  activeTag = null,
  categories = [],
  tags = [],
  // DB-backed authors from getAuthors() (the admin /api/v1/users endpoint).
  // When this list is non-empty we render it directly; otherwise we fall
  // back to the unique author names found in latestdBlogs so the widget
  // still has something to show if the CMS authors endpoint is down.
  authors = [],
}) => {
  const blogCategories = categories;
  const blogTags = tags;
  const latestForAuthors = Array.isArray(latestdBlogs) ? latestdBlogs : [];
  const dbAuthors = Array.isArray(authors) ? authors.filter((a) => a && a.name) : [];
  const blogAuthors = Array.from(
    new Map(
      latestForAuthors.map((b) => [
        b.author && b.author.name ? b.author.name : "Anonymous",
        {
            name: b.author && b.author.name ? b.author.name : "Anonymous",
            img: b.author && b.author.img ? b.author.img : "author.webp",
          },
      ])
    ).values()
  );
  // Prefer DB-fetched authors; fall back to blog-derived only when the
  // CMS endpoint returned nothing (e.g. auth failure, empty users table).
  const sidebarAuthors = dbAuthors.length
    ? dbAuthors.map((a) => ({
        id: a.id,
        slug: a.slug,
        name: a.name,
        role: a.role || "author",
        avatar: resolveAuthorImage(a),
        bio: a.bio || "",
      }))
    : blogAuthors.map((a) => ({
        id: null,
        slug: null,
        name: a.name,
        role: "author",
        avatar: a.img || "",
        bio: "",
      }));
  const product = popularProducts?.[0];
  const agent = product && product.agent ? product.agent : null;
  const price = product && typeof product.price === "number" ? product.price : 0;
  const discount =
    product && typeof product.discount === "number" ? product.discount : 0;
  const discountedPrice = getDiscountPrice(price, discount).toFixed(2);

  return (
    <>
      <aside className="sidebar-area blog-sidebar">
        {/* <!-- Author Widget --> */}
        {showAgent && agent ? (
          <div className="widget ltn__author-widget">
            <div className="ltn__author-widget-inner text-center">
              <SafeImage
                src={`/img/team/${agent.img}`}
                alt={`${agent.fullName}`}
                width={120}
                height={120}
                sizes="120px"
                loading="lazy"
                style={{ width: "120px", height: "120px", objectFit: "cover" }}
              />
              <h3 className="agent-name">{agent.fullName}</h3>
              <small>{agent.designation}</small>
              <div className="product-ratting">
                <ul>
                  <li>
                    <Link href="#">
                      <FaStar />
                    </Link>
                  </li>
                  <li>
                    <Link href="#">
                      <FaStar />
                    </Link>
                  </li>
                  <li>
                    <Link href="#">
                      <FaStar />
                    </Link>
                  </li>
                  <li>
                    <Link href="#">
                      <FaStar />
                    </Link>
                  </li>
                  <li>
                    <Link href="#">
                      <FaRegStar />
                    </Link>
                  </li>
                  <li className="review-total">
                    <Link href="#">( {agent.raiting} Reviews )</Link>
                  </li>
                </ul>
              </div>
              <p>{agent.description}</p>

              <div className="ltn__social-media">
                <ul>
                  <li>
                    <Link href="#" title="Facebook">
                      <FaFacebookF />
                    </Link>
                  </li>
                  <li>
                    <Link href="#" title="Twitter">
                      <FaTwitter />
                    </Link>
                  </li>
                  <li>
                    <Link href="#" title="Linkedin">
                      <FaInstagram />
                    </Link>
                  </li>

                  <li>
                    <Link href="#" title="Youtube">
                      <FaDribbble />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ) : null}
        {/* <!-- Search Widget --> */}
        <div className="widget ltn__search-widget">
          <h2 className="ltn__widget-title ltn__widget-title-border-2">
            Search Blog
          </h2>
          <form action="#">
            <input
              type="text"
              name="search"
              placeholder="Search your keyword..."
            />
            <button type="submit" aria-label="Search">
              <FaSearch />
            </button>
          </form>
        </div>
        {/* <!-- Form Widget --> */}
        {showBookForm ? (
          <div className="widget ltn__form-widget">
            <h2 className="ltn__widget-title ltn__widget-title-border-2">
              Drop Messege For Book
            </h2>
            <form action="#">
              <input type="text" name="yourname" placeholder="Your Name*" />
              <input type="text" name="youremail" placeholder="Your e-Mail*" />
              <textarea
                name="yourmessage"
                placeholder="Write Message..."
              ></textarea>
              <button type="submit" className="btn theme-btn-1">
                Send Messege
              </button>
            </form>
          </div>
        ) : null}
        {/* <!-- Top Rated Product Widget --> */}
        {showTopRated ? (
          <div className="widget ltn__top-rated-product-widget">
            <h2 className="ltn__widget-title ltn__widget-title-border-2">
              Top Rated Product
            </h2>
            <ul>
              {topRatedProducts.map((product, keys) => {
                const slug = productSlug(product.title);
                let key = keys + 1;
                return (
                  <li key={product.id}>
                    <div className="top-rated-product-item clearfix">
                      <div className="top-rated-product-img">
                        <Link href={`/projects/${slug}`}>
                          <SafeImage
                            src={`/img/product/${key}.webp`}
                            alt={product.title}
                            width={80}
                            height={80}
                            sizes="80px"
                            loading="lazy"
                            style={{ width: "80px", height: "80px", objectFit: "cover" }}
                          />
                        </Link>
                      </div>
                      <div className="top-rated-product-info">
                        <div className="product-ratting">
                          <ul>
                            <li>
                              <Link href="#">
                                <FaStar />
                              </Link>
                            </li>
                            <li>
                              <Link href="#">
                                <FaStar />
                              </Link>
                            </li>
                            <li>
                              <Link href="#">
                                <FaStar />
                              </Link>
                            </li>
                            <li>
                              <Link href="#">
                                <FaStar />
                              </Link>
                            </li>
                            <li>
                              <Link href="#">
                                <FaStar />
                              </Link>
                            </li>
                          </ul>
                        </div>
                        <h3 className="popular-post-title">
                          <Link href={`/projects/${slug}`}>{product.title}</Link>
                        </h3>
                        <div className="product-price">
                          <span>${product.price}</span>
                          <del>${discountedPrice}</del>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        {/* <!-- Menu Widget (Category) --> */}
        <div className="widget ltn__menu-widget">
          <h2 className="ltn__widget-title ltn__widget-title-border-2">
            Categories
          </h2>
          <ul>
            {blogCategories.map((cat, key) => {
              // CMS categories come as {name, slug, count}; the legacy
              // getIndividualCategories list only has {name, count}.
              const name = typeof cat === "string" ? cat : cat.name;
              const slug =
                typeof cat === "string"
                  ? productSlug(cat)
                  : cat.slug || productSlug(cat.name);
              const isActive =
                activeCategory &&
                String(activeCategory).toLowerCase() === slug;
              return (
                <li key={key} className={isActive ? "widget-active" : null}>
                  <Link href={`/category/${slug}`}>
                    {String(name).charAt(0).toUpperCase() + String(name).slice(1)}
                    <span>({cat.count ?? 0})</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* <!-- Author Widget — removed --> */}
        {/* <!-- Popular Product Widget — removed; showed "Properties" heading
             on the blog page which conflicted with the projects page. --> */}
        {/* <!-- Popular Post Widget --> */}
        <div className="widget ltn__popular-post-widget">
          <h2 className="ltn__widget-title ltn__widget-title-border-2 latest-blogs-title">
            Latest Blogs
          </h2>
          <ul className="latest-blogs-list">
            {latestdBlogs.map((blog, key) => {
              const slug = blog.slug || productSlug(blog.title);
              const imgSrc = resolveBlogImage(blog);

              return (
                <li key={blog.id || key} className="latest-blogs-card">
                  <Link
                    href={`/blog/${slug}`}
                    className="latest-blogs-card__link"
                    aria-label={blog.title}
                  >
                    {imgSrc ? (
                      <SafeImage
                        src={imgSrc}
                        alt={blog.title}
                        width={120}
                        height={90}
                        sizes="120px"
                        loading="lazy"
                        className="latest-blogs-card__img"
                      />
                    ) : (
                      <span
                        className="latest-blogs-card__placeholder"
                        aria-hidden="true"
                      >
                        <FaRegUser />
                      </span>
                    )}
                    <span
                      className="latest-blogs-card__overlay"
                      aria-hidden="true"
                    />
                    <span className="latest-blogs-card__body">
                      <span className="latest-blogs-card__title">
                        {blog.title}
                      </span>
                      <span className="latest-blogs-card__date">
                        <FaCalendarAlt />
                        {blog.date}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {showFollowUs ? <FollowUs title="Follow Siddhivinayak Group on Social Media" /> : null}
      </aside>
    </>
  );
};

export default BlogSideBar;
